import React, { useRef, useState } from 'react';
import { CDN2 } from '@/helpers/api';
import { basicPageProps, fetchTemplate } from '@/utils/isServer';
import { Breadcrumb, Form, Input, Button, Select, Radio, message } from 'antd';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import CreatorsTable from '@/components/CreatorsTable';
import ReCAPTCHA from "react-google-recaptcha";
import { modalType } from '@/components/Modal';
const { TextArea } = Input;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { configInfo, airports } = await basicPageProps(ctx);

  return {
    props: {
      airports,
      recaptchaSiteKey: configInfo.recaptchaSiteKey,
    },
  };
};

function Affiliate({recaptchaSiteKey}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const captchaRef = useRef(null);

  const handleSubmit =  async (values: any) => {
    let recaptchaToken = '';
    if (captchaRef.current) {
      try {
        recaptchaToken = await captchaRef.current.executeAsync();
      }
      catch (e) {
          modalType({
            type: 'error',
            title: 'Error',
            content: (
              <div className="text-left">
                <p>Error</p>
                <p>
                  Captcha error
                </p>
              </div>
            ),
          });
        captchaRef.current.reset();
        return;
      }
      captchaRef.current.reset();
    }
    setLoading(true);
    const params = {
      type: 1,
      company: form.getFieldValue('company'),
      fullName: form.getFieldValue('fullName'),
      email: form.getFieldValue('email'),
      website: form.getFieldValue('website'),
      businessType: form.getFieldValue('businessType'),
      traffic: form.getFieldValue('traffic'),
      promotionPlan: form.getFieldValue('promotionPlan'),
      whiteLabelInterest: form.getFieldValue('whiteLabelInterest'),
      recaptchaToken
    };

    fetch('/api/Subscriber/RegisterGeneric', {
      ...fetchTemplate,
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((success) => {
        setLoading(false);
        // setSendingForm(false);
        message.success('Affiliate application submitted!');
        form.resetFields();
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  return (
    <div>
      <Head>
        <title>Affiliate | On Air Parking</title>
        <meta
          name="description"
          content="We believe the only way to create partnerships is through win-win relatitonships. Partner with On Air Parking."
          key="description"
        />
        <meta
          property="og:url"
          content="https://www.onairparking.com/partner"
          key="og:url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta
          property="og:title"
          content="Creators Partner | On Air Parking"
          key="og:title"
        />
        <meta
          property="og:description"
          content="We believe the only way to create partnerships is through win-win relatitonships. Partner with On Air Parking."
          key="og:description"
        />
        <meta
          property="og:image"
          content={CDN2() + '/staticmyapp/oglogo-white.png'}
          key="og:image"
        />
      </Head>
      <div className="container mx-auto w-full md:max-w-6xl p-4">
        <Breadcrumb className="text-sm md:text-base mt-4 mb-8">
          <Breadcrumb.Item>
            <a href="/">Home</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Affiliate</Breadcrumb.Item>
        </Breadcrumb>
        <h2 className="text-xl lg:text-3xl font-bold">
          Earn More With On Air Parking
        </h2>
        <p className="text-lg">
          Join travel agencies, car rental companies, and airlines who are already earning through affiliate partnerships with On Air Parking. We make it simple: no upfront costs, no minimums, and no hassle.
        </p>
        <div className="mb-6">
          <h3 className="text-lg font-semibold mt-4">Perks</h3>
          <ul className="list-disc list-inside text-base ml-4 mt-2">
            <li>$5.00 + 5% commission per first-time purchase</li>
            <li>Real-time tracking</li>
            <li>No minimums or reporting fees</li>
            <li>Get paid monthly</li>
            <li>Exclusive deals for your followers</li>
            <li>
              Optional co-branded campaigns and guest blog or email promo
              features
            </li>
          </ul>

          <h3 className="text-lg font-semibold mt-6">
            Earnings Structure Reminder
          </h3>
          <p className="text-base mt-2">
            Earn <strong>$5 per reservation</strong> +{' '}
            <strong>5% of net sales</strong> (daily parking rate) for every
            first-time customer booking using your promo code or custom link.
          </p>
          <p className="text-sm text-gray-600 mt-1">
            <strong>Note:</strong> Discounts are for one-time use per customer.
            Commissions apply to first-time purchases only.
          </p>
          <CreatorsTable />

        </div>
        <div>
        </div>
        <div className="mb-8">
          <h3 className="text-lg font-semibold mt-8 mb-2">How It Works</h3>
          <ol className="list-decimal list-inside text-base ml-4">
            <li>Fill out the form</li>
            <li>We review your website and content</li>
            <li>
              If approved, receive a formal agreement and onboarding materials
            </li>
          </ol>
          <h3 className="text-xl font-semibold my-4">Affiliate Application</h3>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="space-y-4"
            >
              <Form.Item
                name="company"
                label="Company Name"
                hasFeedback
                rules={[
                  { required: true, message: 'Please enter your company name' },
                ]}
              >
                <Input placeholder="Company Inc." aria-label="Company Name" />
              </Form.Item>

              <Form.Item
                name="fullName"
                label="Full Name"
                hasFeedback
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input placeholder="Jane Smith" aria-label="Full Name" />
              </Form.Item>

              <Form.Item
                name="title"
                label="Title"
                hasFeedback
                rules={[{ required: true, message: 'Please enter your title' }]}
              >
                <Input placeholder="Marketing Manager" aria-label="Title" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                hasFeedback
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
              >
                <Input placeholder="your@email.com" aria-label="Email" />
              </Form.Item>

              <Form.Item
                name="website"
                label="Website"
                hasFeedback
                rules={[
                  { required: true, message: 'Please enter your website' },
                ]}
              >
                <Input
                  placeholder="https://yourcompany.com"
                  aria-label="Website"
                />
              </Form.Item>

              <Form.Item
                name="businessType"
                label="Type of Business"
                hasFeedback
                rules={[
                  { required: true, message: 'Please select a business type' },
                ]}
              >
                <Select
                  placeholder="Select one"
                  className="border rounded-md"
                  aria-label="Type of Business"
                >
                  <Select.Option value="Travel Agency">
                    Travel Agency
                  </Select.Option>
                  <Select.Option value="Car Rental">Car Rental</Select.Option>
                  <Select.Option value="Airline">Airline</Select.Option>
                  <Select.Option value="Influencer">Influencer</Select.Option>
                  <Select.Option value="Blog/Publisher">
                    Blog/Publisher
                  </Select.Option>
                  <Select.Option value="Other">Other</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="traffic"
                label="Approx. Monthly Website Traffic / Clients Served"
                hasFeedback
                rules={[
                  { required: true, message: 'Please provide this info' },
                ]}
              >
                <Input
                  placeholder="e.g., 20,000 monthly visitors or 1,000 clients"
                  aria-label="Monthly Website Traffic or Clients Served"
                />
              </Form.Item>

              <Form.Item
                name="promotionPlan"
                label="How do you plan to promote On Air Parking?"
                hasFeedback
                rules={[{ required: true, message: 'Please share your plan' }]}
              >
                <TextArea
                  rows={3}
                  placeholder="Social media, newsletter, website banner..."
                  aria-label="Promotion Plan"
                />
              </Form.Item>

              <Form.Item
                name="whiteLabelInterest"
                label="Are you interested in a custom or white-label solution?"
                hasFeedback
                rules={[{ required: true, message: 'Please select an option' }]}
              >
                <Select
                  placeholder="Select one"
                  className="border rounded-md"
                  aria-label="Custom or White-label Interest"
                >
                  <Select.Option value="Yes">Yes</Select.Option>
                  <Select.Option value="No">No</Select.Option>
                  <Select.Option value="Maybe">Maybe</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item>
                <div className="justify-items-center">
                  {!!recaptchaSiteKey && (
                    <ReCAPTCHA
                      sitekey={recaptchaSiteKey}
                      ref={captchaRef}
                      size="invisible"
                    />
                  )}
                </div>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  className="bg-primary text-white"
                >
                  Submit Affiliate Application
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Affiliate;
